/**
 * composerDraft — the operator's unsent text survives a remount.
 *
 * 2026-08-29 (Dan: "wright just added an arc fix that wiped out my previous text"): the composer held its draft in
 * `useState('')` with no persistence, and `ChatConsoleContext.tsx` is a mixed-export module (Provider + hooks), so
 * every Vite Fast Refresh of it REMOUNTS the console subtree — draft gone. Production has the same exposure on any
 * deploy while an operator is mid-sentence. The draft is kept per persona in sessionStorage: written on every change,
 * restored on mount / persona switch, cleared on send. sessionStorage (not localStorage): a draft belongs to this tab
 * and this session, never to the next login. Every access is try/catch — storage can throw (private mode, quota,
 * a blocked origin) and the composer must render regardless.
 */
const DRAFT_PREFIX = 'nexemerge.composer.draft:';

export function draftStorageKey(draftKey: string | undefined): string | null {
  const k = (draftKey ?? '').trim();
  return k.length > 0 ? `${DRAFT_PREFIX}${k}` : null;
}

export function readDraft(draftKey: string | undefined): string {
  const key = draftStorageKey(draftKey);
  if (!key) return '';
  try {
    return window.sessionStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

export function writeDraft(draftKey: string | undefined, text: string): void {
  const key = draftStorageKey(draftKey);
  if (!key) return;
  try {
    if (text.length === 0) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, text);
  } catch {
    /* storage unavailable — the in-memory draft still works for this mount */
  }
}

export function clearDraft(draftKey: string | undefined): void {
  const key = draftStorageKey(draftKey);
  if (!key) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* nothing to clear */
  }
}

/** Logout: the drafts belong to the operator who typed them, never to the next login in the same tab. */
export function clearAllDrafts(): void {
  try {
    const store = window.sessionStorage;
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && key.startsWith(DRAFT_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) store.removeItem(key);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
